// components/slides/SlidesTrialNotice.tsx

'use client';

import React from 'react';
import styles from '@/components/portal/Notice.module.css';

import {
  TrialFeedbackLink,
  TrialNotice,
  TrialNoticeSignature,
} from '@/components/portal/TrialNotice';

/** Pill text in the left nav, and the name used in the notice. */
export const SLIDES_TRIAL_LABEL = 'Limited Trial';

const B = ({ children }: { children: React.ReactNode }) => <strong className="font-medium text-white">{children}</strong>;

/** The pilot launch welcome (2026-10-07), in the reader's language: French for the three French RDMs. */
const LAUNCH = {
  en: {
    title: 'Welcome to Slides',
    intro: 'Slides reviews tenant design proposals against the Westfield 10-point guidelines. The Reviewer drafts, you decide: every comment, verdict and stamp stays yours to change.',
    steps: [
      [<B key="b">Import.</B>, " Drop the tenant's PDF on the Slides page, or press Import PDF."],
      [<B key="b">Review.</B>, ' Press Launch review. The Reviewer looks up the brand, labels every sheet, comments on the drawings and fills the validation stamp. The bar above the chat shows how far it has got, and Continue review picks up a review that stopped.'],
      [<B key="b">Correct.</B>, ' Double-click any comment or stamp cell to change it. Your corrections teach the Reviewer.'],
      [<B key="b">Deliver.</B>, " Export the Review PDF: our cover page (validation stamp, Better Places, Tenant Survey, cartouche) in front of the tenant's pages and your comments. More stamps are in the stamp library, the last button of the toolbar."],
    ],
    note: ["Slides is in beta, you will run into bugs. Don't hesitate to let us know at ", ''],
    pilot: 'To run a successful pilot, please use the tool on real world use cases and report to us any feedback for improvement.',
    fun: 'Have fun!',
    close: 'Got it',
    tour: 'Start tour',
  },
  fr: {
    title: 'Bienvenue dans Slides',
    intro: "Slides analyse les propositions design des preneurs selon les 10 points des guidelines Westfield. Le Reviewer propose, vous décidez : chaque commentaire, verdict et case du tampon reste modifiable.",
    steps: [
      [<B key="b">Importer.</B>, ' Déposez le PDF du preneur sur la page Slides, ou cliquez sur Import PDF.'],
      [<B key="b">Analyser.</B>, " Cliquez sur Launch review. Le Reviewer recherche l'enseigne, nomme chaque planche, commente les plans et remplit le tampon de validation. La barre au-dessus du chat indique l'avancement, et Continue review reprend une analyse interrompue."],
      [<B key="b">Corriger.</B>, ' Double-cliquez sur un commentaire ou une case du tampon pour le modifier. Vos corrections font progresser le Reviewer.'],
      [<B key="b">Livrer.</B>, " Exportez le Review PDF : notre page de garde (tampon de validation, Better Places, Tenant Survey, cartouche) devant les pages du preneur et vos commentaires. D'autres tampons sont dans la bibliothèque, le dernier bouton de la barre d'outils."],
    ],
    note: ["Slides est en bêta, vous rencontrerez des bugs. N'hésitez pas à nous les signaler à ", '.'],
    pilot: "Pour réussir ce pilote, utilisez l'outil sur de vrais dossiers et faites-nous part de toute suggestion d'amélioration.",
    fun: 'Bonne découverte !',
    close: 'Compris',
    tour: 'Visite guidée',
  },
} as const;

export const slidesLaunchTourLabel = (language: 'en' | 'fr') => LAUNCH[language].tour;

export const SlidesTrialNotice = ({ onClose, language = 'en', actions }: { onClose: () => void; language?: 'en' | 'fr'; actions?: React.ReactNode }) => {
  const t = LAUNCH[language];
  return (
    <TrialNotice label={t.title} onClose={onClose} actions={actions} closeLabel={t.close}>
      <p style={{ marginBottom: 16 }}>{t.intro}</p>
      <ol className={styles.updates}>
        {t.steps.map((step, i) => <li key={i}>{step}</li>)}
      </ol>
      {/* The CEO's note: wording from his original trial message; the French is its translation. */}
      <p>{t.note[0]}<TrialFeedbackLink subject="Slides feedback" />{t.note[1]}</p>
      <p>{t.pilot}</p>
      <p>{t.fun}</p>
      <TrialNoticeSignature />
    </TrialNotice>
  );
};
